import { Flex, type SpaceProps, Table, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden } from "@chakra-ui/react";
import React from "react";
import { getTransparentBrandColorHex } from "theme/colors";
import { ErrorIcon, SuccessIcon } from "ui/icons";
import { useCdpIndustryConsiderationRows } from "./data";

const greenBg = getTransparentBrandColorHex("green", 50, 0.5);
const cellPx: SpaceProps["px"] = { base: "bigger", lg: "biggest" };

type DataCellProps = {
  isLastRow?: boolean;
  isComposableCdp?: boolean;
  title: string;
  description: string;
};

function DataCell({ isComposableCdp, isLastRow, title, description }: DataCellProps) {
  return (
    <Td
      py="bigger"
      px={cellPx}
      verticalAlign="top"
      bg={isComposableCdp ? greenBg : undefined}
      sx={isLastRow ? { borderBottomRadius: "small" } : undefined}
    >
      <Flex gap="smallest">
        {isComposableCdp ? <SuccessIcon color="new.green.700" /> : <ErrorIcon color="new.red.700" />}
        {description}
      </Flex>
    </Td>
  );
}

export function ConsiderationsTable() {
  const tableRows = useCdpIndustryConsiderationRows();

  return (
    <Table variant="unstyled" maxW="container.section" mx="auto">
      <Thead>
        <Tr>
          <Th>
            <VisuallyHidden>Category</VisuallyHidden>
          </Th>
          <Th py="50px" pl={cellPx}>
            <Text as="span" textStyle="headline2" textTransform="none">
              Traditional CDP
            </Text>
          </Th>
          <Th py="50px" pl={cellPx} bg={greenBg} borderTopRadius="small">
            <Text as="span" textStyle="headline2" textTransform="none">
              Composable CDP
            </Text>
          </Th>
        </Tr>
      </Thead>

      <Tbody>
        {tableRows.map((row, idx) => (
          <Tr key={row.title} textStyle="caption1" borderTop="1px" borderColor="new.gray.150">
            <Td fontWeight="bold" py="bigger">
              {row.title}
            </Td>
            <DataCell title={row.title} description={row.considerations.traditional} />
            <DataCell
              isComposableCdp
              title={row.title}
              isLastRow={idx === tableRows.length - 1}
              description={row.considerations.composable}
            />
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
